<script setup lang="ts">
import { defineProps, onMounted, ref } from 'vue'
import { printPhone } from '@/utils/phones'
import ButtonCallTo from '@/components/common/buttons/ButtonCallTo.vue'
import ButtonMailTo from '@/components/common/buttons/ButtonMailTo.vue'
import IconWithTooltip from '@/components/common/ui/IconWithTooltip.vue'
import type { Partner } from '@/api-swagger'
import {
  DevicePhoneMobileIcon,
  ListBulletIcon,
  MapPinIcon,
  MegaphoneIcon,
  ViewfinderCircleIcon,
} from '@heroicons/vue/24/outline'
import TodoModal from '@/components/features/todos/TodoModal.vue'
import AddToCampaignButton from '@/components/common/buttons/AddToCampaignButton.vue'
import AddCompanyToCampaignModal from '@/components/features/campaigns-bc/AddCompanyToCampaignModal.vue'
import { storeToRefs } from 'pinia'
import { useCampaignStore } from '@/stores/useCampaignStore'
import { usePartnerStore } from '@/stores/usePartnerStore'

const campaignStore = useCampaignStore()
const partnerStore = usePartnerStore()
const { loadingAddCallSessions } = storeToRefs(campaignStore)

const props = defineProps({
  partner: {
    type: Object as () => Partner,
    required: true,
  },
  geoloc: {
    type: Boolean,
    default: false,
  },
  activity: {
    type: Boolean,
    default: false,
  },
  /** When provided, the new boost-core "Add to campaign" modal is used.
   *  Otherwise, falls back to the legacy V1 multi-select dropdown. */
  companyUuid: {
    type: String,
    default: '',
  },
})

const partnerId = ref()
const company = ref()

const showTodoModal = ref(false)
const addToCampaignOpen = ref(false)

const openTodoModal = async() => {
  if(props.partner)
    company.value = await partnerStore.fetchCompanyByOdooRef(props.partner.id)
  showTodoModal.value = true
}

const closeTodoModal = () => {
  company.value = undefined
  showTodoModal.value = false
}

const handleTodoCreated = () => {
  showTodoModal.value = false
}

onMounted(() => {
  partnerId.value = props.partner.id
})
</script>

<template>
  <div class="flex justify-center items-center gap-2">
    <ButtonCallTo
      v-if="partner.phone"
      :partner-id="partnerId"
      :contact-id="partner.id"
      :phone="printPhone(partner.phone)"
    />
    <ButtonCallTo
      v-if="partner.mobile"
      :partner-id="partnerId"
      :contact-id="partner.id"
      :phone="printPhone(partner.mobile)"
      :icon="DevicePhoneMobileIcon"
    />
    <ButtonMailTo v-if="partner.email" :email="partner.email" />

    <a
      v-if="geoloc && partner.partnerLatitude && partner.partnerLongitude"
      :href="`https://www.google.com/maps/search/?api=1&query=${partner.partnerLatitude},${partner.partnerLongitude}`"
      target="_blank"
    >
      <IconWithTooltip :text="'Google map: ' + partner.contactAddress" :icon="MapPinIcon" />
    </a>
    <IconWithTooltip v-if="activity" :text="'Nouveau To Do'" @click="openTodoModal">
      <ListBulletIcon class="h-4 w-4"></ListBulletIcon>
    </IconWithTooltip>
    <IconWithTooltip
      v-if="companyUuid"
      text="Ajouter à une campagne"
      @click="addToCampaignOpen = true"
    >
      <component :is="MegaphoneIcon" class="h-4 w-4" />
    </IconWithTooltip>
    <AddToCampaignButton
      v-else
      :selectedPartners="[partner]"
      :loader="loadingAddCallSessions"
    />
    <AddCompanyToCampaignModal
      v-if="companyUuid"
      :open="addToCampaignOpen"
      :company-uuid="companyUuid"
      :company-name="partner.name"
      @close="addToCampaignOpen = false"
    />
    <a
      v-if="partner.partnerLatitude && partner.partnerLongitude"
      :href="`/campaigns/management?lat=${partner.partnerLatitude}&lng=${partner.partnerLongitude}`"
      target="_blank"
    >
      <IconWithTooltip text="Rechercher autour" :icon="ViewfinderCircleIcon" />
    </a>
    <TodoModal
      v-if="company"
      :showModal="showTodoModal"
      mode="create"
      @close="closeTodoModal"
      @created="handleTodoCreated"
      :company="company"
    />
  </div>

</template>
